import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const clone = (x) => JSON.parse(JSON.stringify(x));

export default function AdminDelivery() {
  const [kw, setKw] = useState([]);
  const [gcc, setGcc] = useState([]);
  const [localFee, setLocalFee] = useState("3.000");
  const [intlFee, setIntlFee] = useState("15.000");
  const [saved, setSaved] = useState(null); // last saved snapshot
  const [saving, setSaving] = useState(false);

  const snapshot = () => JSON.stringify({ kw, gcc, localFee: String(localFee), intlFee: String(intlFee) });
  const dirty = saved !== null && snapshot() !== saved;

  const apply = (data) => {
    setKw(data.delivery_kuwait || []);
    setGcc(data.delivery_gcc || []);
    setLocalFee(String(data.local_delivery_fee ?? 3));
    setIntlFee(String(data.intl_shipping_fee ?? 15));
    setSaved(JSON.stringify({
      kw: data.delivery_kuwait || [], gcc: data.delivery_gcc || [],
      localFee: String(data.local_delivery_fee ?? 3), intlFee: String(data.intl_shipping_fee ?? 15),
    }));
  };

  const load = () => api.get("/settings").then((r) => apply(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // warn on leaving with unsaved changes
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const valid = () => {
    const bad = (v) => v === "" || isNaN(Number(v)) || Number(v) < 0;
    if (bad(localFee) || bad(intlFee)) return "رسوم التوصيل/الشحن الافتراضية يجب أن تكون أرقاماً غير سالبة";
    for (const a of kw) if (bad(a.fee)) return `رسوم منطقة "${a.area || "?"}" غير صالحة`;
    for (const g of gcc) if (bad(g.fee)) return `رسوم دولة "${g.country || "?"}" غير صالحة`;
    return null;
  };

  const save = async () => {
    const err = valid();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/delivery", {
        delivery_kuwait: kw.map((a) => ({ ...a, fee: Number(a.fee) })),
        delivery_gcc: gcc.map((g) => ({ ...g, fee: Number(g.fee) })),
        local_delivery_fee: Number(localFee),
        intl_shipping_fee: Number(intlFee),
      });
      apply(data); // reflect the server-confirmed values only after success
      toast.success("تم حفظ إعدادات التوصيل والشحن");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const cancel = () => {
    if (!saved) return;
    const s = JSON.parse(saved);
    setKw(clone(s.kw)); setGcc(clone(s.gcc)); setLocalFee(s.localFee); setIntlFee(s.intlFee);
    toast.info("تم التراجع عن التغييرات");
  };

  const upKw = (i, k, v) => setKw(kw.map((a, idx) => idx === i ? { ...a, [k]: v } : a));
  const upGcc = (i, k, v) => setGcc(gcc.map((g, idx) => idx === i ? { ...g, [k]: v } : g));

  return (
    <div className="p-5 md:p-8 max-w-3xl">
      <div className="flex items-center justify-between mb-1 sticky top-0 bg-surface/80 backdrop-blur z-10 py-2">
        <h1 className="font-display text-3xl">التوصيل والشحن</h1>
        <div className="flex items-center gap-2">
          {dirty && <button onClick={cancel} data-testid="cancel-delivery-btn" className="flex items-center gap-1 border border-border px-3 py-2.5 text-sm"><RotateCcw className="w-4 h-4" /> إلغاء</button>}
          <button onClick={save} disabled={saving || !dirty} data-testid="save-delivery-btn" className="flex items-center gap-2 bg-[#141110] text-white px-4 py-2.5 text-sm disabled:opacity-50"><Save className="w-4 h-4" /> {saving ? "جارٍ الحفظ…" : "حفظ التغييرات"}</button>
        </div>
      </div>
      {dirty && <p className="text-xs text-amber-600 mb-4" data-testid="delivery-dirty">لديك تغييرات غير محفوظة — اضغطي «حفظ التغييرات».</p>}

      <div className="bg-white border border-border p-5 mb-6">
        <h2 className="text-sm tracking-luxe uppercase text-accent mb-3">التعرفة المعتمدة (تُطبّق في الكاشير والطلبات الجديدة)</h2>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-xs text-text-muted block mb-1">رسوم التوصيل داخل الكويت (KWD)</label>
            <input type="number" step="0.001" min="0" value={localFee} onChange={(e) => setLocalFee(e.target.value)} data-testid="local-fee" className="w-full border border-border px-3 py-2 text-sm" /></div>
          <div><label className="text-xs text-text-muted block mb-1">رسوم الشحن الدولي (KWD)</label>
            <input type="number" step="0.001" min="0" value={intlFee} onChange={(e) => setIntlFee(e.target.value)} data-testid="intl-fee" className="w-full border border-border px-3 py-2 text-sm" /></div>
        </div>
        <p className="text-[11px] text-text-muted mt-2">هذه التعرفة الافتراضية الموحّدة: تُطبّق في الكاشير، وفي طلبات الموقع عند عدم وجود سعر فعّال للمحافظة أو الدولة أدناه. إذا وُجد سعر للوجهة فهو المعتمد في ملخص العميلة والدفع والطلب والفاتورة. الفواتير والطلبات السابقة تبقى بقيمها كما صدرت.</p>
      </div>

      <div className="bg-white border border-border p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm tracking-luxe uppercase text-accent">محافظات الكويت (تُعتمد لطلبات الموقع حسب محافظة العميلة)</h2>
          <button onClick={() => setKw([...kw, { area: "", fee: 0, note: "", active: true }])} data-testid="add-kw-area" className="flex items-center gap-1 text-xs border border-border px-2 py-1"><Plus className="w-3 h-3" /> منطقة</button>
        </div>
        {kw.map((a, i) => (
          <div key={i} className="flex items-center gap-2 mb-2 min-w-0" data-testid={`kw-row-${i}`}>
            <input value={a.area} onChange={(e) => upKw(i, "area", e.target.value)} placeholder="المنطقة" className="flex-1 min-w-0 border border-border px-2 py-1.5 text-sm" />
            <input type="number" step="0.001" min="0" value={a.fee} onChange={(e) => upKw(i, "fee", e.target.value)} placeholder="الرسوم" data-testid={`kw-fee-${i}`} className="w-20 shrink-0 border border-border px-2 py-1.5 text-sm" />
            <label className="flex items-center gap-1 text-xs shrink-0"><input type="checkbox" checked={a.active !== false} onChange={(e) => upKw(i, "active", e.target.checked)} /> فعّال</label>
            <button onClick={() => setKw(kw.filter((_, idx) => idx !== i))} className="text-text-muted hover:text-destructive shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <div className="bg-white border border-border p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm tracking-luxe uppercase text-accent">دول الخليج (KWD + مدة الشحن)</h2>
          <button onClick={() => setGcc([...gcc, { country: "", fee: 0, transit: "", note: "", active: true }])} data-testid="add-gcc" className="flex items-center gap-1 text-xs border border-border px-2 py-1"><Plus className="w-3 h-3" /> دولة</button>
        </div>
        <p className="text-[11px] text-text-muted mb-3 md:hidden" data-testid="gcc-scroll-hint">↔ اسحبي الجدول أفقياً لعرض كل الحقول</p>
        <div className="overflow-x-auto -mx-5 px-5 pb-2" data-testid="gcc-scroll">
        <div className="min-w-[600px]">
        <div className="grid grid-cols-[140px_90px_1fr_60px_24px] gap-2 mb-1 text-[11px] text-text-muted"><span>الدولة</span><span>الرسوم (KWD)</span><span>مدة الشحن</span><span>الحالة</span><span /></div>
        {gcc.map((g, i) => (
          <div key={i} className="grid grid-cols-[140px_90px_1fr_60px_24px] items-center gap-2 mb-2" data-testid={`gcc-row-${i}`}>
            <input value={g.country} onChange={(e) => upGcc(i, "country", e.target.value)} placeholder="الدولة" className="w-full border border-border px-2 py-1.5 text-sm" />
            <input type="number" step="0.001" min="0" value={g.fee} onChange={(e) => upGcc(i, "fee", e.target.value)} placeholder="الرسوم" data-testid={`gcc-fee-${i}`} className="w-full border border-border px-2 py-1.5 text-sm" />
            <input value={g.transit} onChange={(e) => upGcc(i, "transit", e.target.value)} placeholder="مدة الشحن" data-testid={`gcc-transit-${i}`} className="w-full border border-border px-2 py-1.5 text-sm" />
            <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={g.active !== false} onChange={(e) => upGcc(i, "active", e.target.checked)} /> فعّال</label>
            <button onClick={() => setGcc(gcc.filter((_, idx) => idx !== i))} className="text-text-muted hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        </div>
        </div>
      </div>
    </div>
  );
}
